import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import * as v from 'valibot';
import { useLingui } from '@lingui/react/macro';
import { useNgoProjectQuery, useNgoProfileQuery } from '@/lib/data-hooks';
import { trpc } from '@/lib/trpc';
import { Suspense, useState } from 'react';
import { ChevronRightIcon } from 'lucide-react';
import { generateCaptchaToken } from '@/lib/captcha';
import { MainSectionHeader, MainSectionContent } from './-components/main-section';
import { ProjectActionsMenu } from './-components/project-actions-menu';
import { VerticalTabs } from './-components/vertical-tabs';
import { ProjectStatusBadge } from './-components/project-status-badge';
import { FormHandleProvider, useFormHandle, useRegisterFormHandle } from './-components/form-context';
import { EditToggleButton } from './-components/edit-toggle-button';
import { LanguageDropdown, AiPendingHint, getAiLangsForFields } from './-components/language-dropdown';
import { getTranslatedText } from '@worldcoo/monolith/core';
import { fillTranslatable, collectTranslatableEntries, autoTranslateMissingTranslations } from '@/lib/locale-utils';
import { uploadEditProjectFiles } from '@/lib/upload-to-s3';
import { generateImageSrc } from '@/lib/shared';
import { useAppForm } from '@/lib/use-app-form';
import { FormReadOnlyProvider } from '@worldcoo/ui/components/form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@worldcoo/ui/components/card';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@worldcoo/ui/components/alert-dialog';
import { EditFooter } from './-components/edit-footer';
import { toTranslatable, toOptTranslatable, projectFormSchema } from '@/lib/project-schemas';
import { showTranslationToast, showTranslationErrorToast } from './-components/translation-toast';

import { PreviewButton } from './-components/project-preview-button';
import { ProjectDetailSkeleton } from './-components/project-detail-skeleton';
import { ProjectBasicTab } from './-components/project-basic-tab';
import { ProjectInfoTab } from './-components/project-info-tab';
import { ProjectMultimediaTab } from './-components/project-multimedia-tab';
import { ProjectFundingTab } from './-components/project-funding-tab';
import { projectFormOpts } from './-components/project-form.opts';
import { toast } from '@/lib/toast';
import { useProjectErrorMessage } from './-components/use-project-error-message';

const tabValues = ['funding', 'languages', 'basic', 'info', 'multimedia'] as const;

export const Route = createFileRoute('/ngos/projects/$projectId')({
	validateSearch: v.object({
		tab: v.fallback(v.optional(v.picklist(tabValues)), undefined),
		editing: v.fallback(v.optional(v.boolean()), undefined),
		plang: v.fallback(v.optional(v.string()), undefined),
		addToPortal: v.fallback(v.optional(v.boolean()), undefined),
		fromPortal: v.fallback(v.optional(v.string()), undefined),
	}),
	loader: ({ context, params }) => {
		context.queryClient.ensureQueryData(trpc.ngo.getProject.queryOptions({ projectId: params.projectId }));
		context.queryClient.ensureQueryData(trpc.ngo.getProjectCollected.queryOptions({ projectId: params.projectId }));
		context.queryClient.ensureQueryData(trpc.ngo.getProjectPortals.queryOptions({ projectId: params.projectId }));
		context.queryClient.ensureQueryData(trpc.ngo.getProfile.queryOptions());
		context.queryClient.ensureQueryData(trpc.ngo.getPortals.queryOptions());
	},
	component: RouteComponent,
});

function RouteComponent() {
	return (
		<Suspense fallback={<ProjectDetailSkeleton />}>
			<ProjectDetail />
		</Suspense>
	);
}

function ProjectDetail() {
	const { projectId } = Route.useParams();
	const project = useNgoProjectQuery(projectId);

	return (
		<FormHandleProvider>
			<ProjectDetailInner project={project} />
		</FormHandleProvider>
	);
}

function ProjectDetailInner(props: { project: ReturnType<typeof useNgoProjectQuery> }) {
	const { t } = useLingui();
	const { editing, fromPortal } = Route.useSearch();
	const navigate = useNavigate({ from: '/ngos/projects/$projectId' });
	const { data: portalsData } = useQuery(trpc.ngo.getPortals.queryOptions());
	const ngo = useNgoProfileQuery();
	const isNgoDraft = 'isDraft' in ngo && ngo.isDraft;
	const firstPortal = portalsData && 'portals' in portalsData ? portalsData.portals[0] : undefined;
	const portalColor = firstPortal?.themeBaseColor;
	const ngoPortalId = firstPortal?.id;
	const isEditing = editing === true;
	const formHandle = useFormHandle();

	// Read languages from form state when editing (so newly-added/removed languages show immediately)
	// When not editing, use server data which is authoritative after save
	const rawFormValues = isEditing ? formHandle?.current?.getValues() : undefined;
	const activeDefaultLang =
		(typeof rawFormValues?.defaultLanguage === 'string' ? rawFormValues.defaultLanguage : undefined) ??
		props.project.defaultLanguage;

	return (
		<>
			<MainSectionHeader
				breadcrumbs={
					<div className="flex items-center gap-1 text-sm">
						{fromPortal ? (
							<Link
								to="/ngos/portals/$portalId"
								params={{ portalId: fromPortal }}
								search={{ tab: 'projects' }}
								className="text-muted-foreground hover:text-foreground"
							>
								{t`Campaigns`}
							</Link>
						) : (
							<Link to="/ngos/projects" className="text-muted-foreground hover:text-foreground">{t`Projects`}</Link>
						)}
						<ChevronRightIcon className="text-muted-foreground size-3" />
						<span className="text-primary">
							{getTranslatedText(props.project.name, activeDefaultLang, props.project.defaultLanguage)}
						</span>
					</div>
				}
				title={
					<div className="flex items-center gap-3">
						<h1 className="text-[24px] leading-[38px] font-medium tracking-[-0.2px]">
							{getTranslatedText(props.project.name, activeDefaultLang, props.project.defaultLanguage)}
						</h1>
						<ProjectStatusBadge
							status={props.project.status}
							endDate={props.project.endDate}
							campaignIds={props.project.campaignIds}
							view="global"
						/>
					</div>
				}
				actions={
					<>
						{props.project.fundingType && props.project.fundingType !== 'worldcoo' && (
							<PreviewButton portalColor={portalColor} />
						)}
						<EditToggleButton isEditing={isEditing} navigate={navigate} />
						<ProjectActionsMenu
							projectId={props.project.id}
							projectName={props.project.name}
							displayName={getTranslatedText(props.project.name, activeDefaultLang, props.project.defaultLanguage)}
							status={props.project.status}
							fundingType={props.project.fundingType}
							isNgoDraft={isNgoDraft}
							ngoPortalId={ngoPortalId}
							campaignId={props.project.campaignIds?.[0]}
							view="global"
							onAfterDelete={() =>
								fromPortal
									? navigate({
											to: '/ngos/portals/$portalId',
											params: { portalId: fromPortal },
											search: { tab: 'projects' },
										})
									: navigate({ to: '/ngos/projects' })
							}
							onAfterDuplicate={(newProjectId) =>
								navigate({
									to: '/ngos/projects/$projectId',
									params: { projectId: newProjectId },
									search: fromPortal ? { editing: true, fromPortal } : { editing: true },
								})
							}
							additionalQueryKeys={[trpc.ngo.getProject.queryKey({ projectId: props.project.id })]}
							buttonVariant="ghost"
							buttonSize="icon"
						/>
					</>
				}
			/>
			<MainSectionContent className="md:!flex-row md:gap-12 md:!overflow-hidden">
				<ProjectContent />
			</MainSectionContent>
			<div id="project-footer-portal" />
		</>
	);
}

function ProjectContent() {
	const { projectId } = Route.useParams();
	const { tab, editing, plang, addToPortal, fromPortal } = Route.useSearch();
	const activeTab = tab ?? 'funding';
	const isEditing = editing === true;
	const shouldOpenAddToPortal = isEditing && addToPortal === true && activeTab === 'funding';
	const project = useNgoProjectQuery(projectId);
	const { t } = useLingui();
	const navigate = useNavigate({ from: '/ngos/projects/$projectId' });
	const queryClient = useQueryClient();
	const mutation = useMutation(trpc.ngo.putProject.mutationOptions());
	const deleteMutation = useMutation(trpc.ngo.deleteProject.mutationOptions());
	const translateMutation = useMutation(trpc.translate.translateProjectTexts.mutationOptions());
	const getErrorMessage = useProjectErrorMessage();
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const coverImageUrl = 'coverImageUrl' in project ? project.coverImageUrl : undefined;
	const optimizedCoverImageUrl = coverImageUrl ? generateImageSrc(coverImageUrl, { width: 600 }) : undefined;
	const coverVideoUrl = 'coverVideoUrl' in project ? project.coverVideoUrl : undefined;
	const serverOtherImageUrls = 'otherImageUrls' in project ? project.otherImageUrls : [];

	const langs = project.languages;
	const validationLang = plang ?? project.defaultLanguage;

	const form = useAppForm({
		...projectFormOpts,
		validators: { onSubmit: projectFormSchema(validationLang) },
		defaultValues: {
			name: fillTranslatable(project.name, langs),
			description: fillTranslatable(project.description, langs),
			objectives: fillTranslatable(project.objectives, langs),
			activities: fillTranslatable(project.activities, langs),
			countryCodes: [...project.countryCodes],
			regionNames: (project as { regionNames?: string[] }).regionNames ?? [],
			appliesToEntireCountry: (project as { appliesToEntireCountry?: boolean }).appliesToEntireCountry ?? false,
			cityNames: project.cityNames ? [...project.cityNames] : [],
			beneficiaries: { direct: project.beneficiaries.direct, indirect: project.beneficiaries.indirect },
			beneficiariesDescription: fillTranslatable(project.beneficiariesDescription, langs),
			beneficiaryTypes: project.beneficiaryTypes ? [...project.beneficiaryTypes] : [],
			impactEquivalent: fillTranslatable(project.impactEquivalent, langs),
			category: project.category,
			subcategories: project.subcategories ? [...project.subcategories] : [],
			sdgs: project.sdgs ? [...project.sdgs] : [],
			fundingType: project.fundingType,
			videoUrl: project.videoUrl ?? '',
			projectBudget: project.projectBudget ?? 0,
			claim: fillTranslatable(project.claim, langs),
			shortParagraph: fillTranslatable(project.shortParagraph, langs),
			donationAmounts: project.donationAmounts?.length
				? project.donationAmounts.map((a, i) => ({
						amount: a,
						equivalent: fillTranslatable(project.donationEquivalents?.[i], langs),
					}))
				: [
						{ amount: 5, equivalent: fillTranslatable(undefined, langs) },
						{ amount: 10, equivalent: fillTranslatable(undefined, langs) },
						{ amount: 25, equivalent: fillTranslatable(undefined, langs) },
						{ amount: 50, equivalent: fillTranslatable(undefined, langs) },
					],
			moreInfo: fillTranslatable(project.moreInfo, langs),
			coverImage: undefined as File | '' | undefined,
			coverImageAlt: fillTranslatable(project.coverImageAlt, langs),
			coverVideo: undefined as File | '' | undefined,
			coverVideoAlt: fillTranslatable(project.coverVideoAlt, langs),
			otherImages: serverOtherImageUrls.map((url, i) => ({
				existingKey: (project.otherImages ?? [])[i],
				existingUrl: generateImageSrc(url, { width: 300 }),
				alt: fillTranslatable((project.otherImagesAlt ?? [])[i], langs),
			})) as typeof projectFormOpts.defaultValues.otherImages,
			economicGoal: project.economicGoal ?? 0,
			startDate: project.startDate ?? '',
			endDate: project.endDate ?? '',
			languages: [...project.languages],
			defaultLanguage: project.defaultLanguage,
			aiTranslated: project.aiTranslated ?? {},
		},
		onSubmit: async ({ value }) => {
			const defaultLang = value.defaultLanguage;

			const {
				values: translatedValues,
				translatedLanguages,
				success,
			} = await autoTranslateMissingTranslations({
				values: value,
				defaultLanguage: defaultLang,
				languages: value.languages,
				translate: async (input) =>
					translateMutation.mutateAsync({ ...input, captchaToken: await generateCaptchaToken() }),
			});
			if (translatedLanguages.length > 0) {
				if (success) showTranslationToast(defaultLang, translatedLanguages);
				else showTranslationErrorToast();
			}
			// Upload new files directly to S3 via presigned URLs (parallel)
			const uploaded = await uploadEditProjectFiles(
				{
					coverImage: translatedValues.coverImage,
					coverVideo: translatedValues.coverVideo,
					otherImageFiles: translatedValues.otherImages.map((item) =>
						item.file instanceof File ? item.file : undefined,
					),
				},
				project.id,
			);

			const formData = translatedValues;

			const donationEquivalents = formData.donationAmounts
				.map((d) => toTranslatable(d.equivalent))
				.filter((eq) => Object.values(eq).some((v) => v.length > 0));
			// Build other images for API: uploaded keys or existing keys
			const otherImagesValue: string[] = [];
			const otherImagesAltValue: Record<string, string>[] = [];
			for (let i = 0; i < formData.otherImages.length; i++) {
				const item = formData.otherImages[i]!;
				const key = uploaded.otherImageKeys[i] ?? item.existingKey;
				if (key) otherImagesValue.push(key);
				const cleanAlt = toTranslatable(item.alt);
				if (Object.values(cleanAlt).some((v) => v.length > 0)) {
					otherImagesAltValue.push(cleanAlt);
				}
			}
			const result = await mutation.mutateAsync({
				project: {
					id: project.id,
					ngoId: project.ngoId,
					name: toTranslatable(formData.name),
					description: toTranslatable(formData.description),
					objectives: toTranslatable(formData.objectives),
					activities: toTranslatable(formData.activities),
					languages: formData.languages,
					defaultLanguage: formData.defaultLanguage,
					countryCodes: formData.countryCodes,
					cityNames: formData.cityNames.length > 0 ? formData.cityNames : undefined,
					regionNames: formData.regionNames.length > 0 ? formData.regionNames : undefined,
					beneficiaries: formData.beneficiaries,
					beneficiariesDescription: toOptTranslatable(formData.beneficiariesDescription),
					beneficiaryTypes: formData.beneficiaryTypes.length > 0 ? formData.beneficiaryTypes : undefined,
					impactEquivalent: toOptTranslatable(formData.impactEquivalent),
					category: formData.category,
					subcategories: formData.subcategories.length > 0 ? formData.subcategories : undefined,
					sdgs: formData.sdgs.length > 0 ? formData.sdgs : undefined,
					fundingType: formData.fundingType,
					videoUrl: formData.videoUrl || undefined,
					projectBudget: formData.projectBudget || undefined,
					claim: toOptTranslatable(formData.claim),
					shortParagraph: toOptTranslatable(formData.shortParagraph),
					donationAmounts:
						formData.donationAmounts.length > 0
							? formData.donationAmounts.map((d) => d.amount).filter((a) => a > 0)
							: undefined,
					donationEquivalents: donationEquivalents.length > 0 ? donationEquivalents : undefined,
					moreInfo: toOptTranslatable(formData.moreInfo),
					economicGoal: formData.economicGoal || undefined,
					startDate: formData.startDate || undefined,
					endDate: formData.endDate || undefined,
					coverImage: uploaded.coverImage ?? (formData.coverImage === '' ? undefined : project.coverImage),
					coverImageAlt: toOptTranslatable(formData.coverImageAlt),
					coverVideo: uploaded.coverVideo ?? (formData.coverVideo === '' ? undefined : project.coverVideo),
					coverVideoAlt: toOptTranslatable(formData.coverVideoAlt),
					otherImages: otherImagesValue.length > 0 ? otherImagesValue : undefined,
					otherImagesAlt: otherImagesAltValue.length > 0 ? otherImagesAltValue : undefined,
					status: project.status,
					aiTranslated: Object.keys(formData.aiTranslated).length > 0 ? formData.aiTranslated : undefined,
				},
			});

			if ('error' in result && result.error) {
				toast.error(getErrorMessage(result.error));
				return;
			}

			// We need to use a timeout because of this issue: https://github.com/TanStack/form/issues/1681
			setTimeout(() => form.reset());

			const savedLanguages = form.state.values.languages as string[];
			const keepPlang = plang && savedLanguages.includes(plang) ? plang : undefined;

			await queryClient.invalidateQueries({ queryKey: trpc.ngo.getProject.queryKey({ projectId }) });
			await queryClient.invalidateQueries({ queryKey: trpc.ngo.getProjects.queryKey() });
			navigate({ search: (prev) => ({ ...prev, editing: undefined, plang: keepPlang }) });
		},
	});

	useRegisterFormHandle(form);
	const selectedLang =
		plang ?? (isEditing ? (form.getFieldValue('defaultLanguage') as string) : project.defaultLanguage);
	const activeLanguages = isEditing ? (form.getFieldValue('languages') as string[]) : project.languages;
	const activeDefaultLang = isEditing ? (form.getFieldValue('defaultLanguage') as string) : project.defaultLanguage;

	const approveTranslation = (fieldName: string) => {
		const current = form.getFieldValue('aiTranslated');
		const langs = current[fieldName];
		if (!langs) return;
		const next = langs.filter((l: string) => l !== selectedLang);
		if (next.length > 0) {
			form.setFieldValue('aiTranslated', { ...current, [fieldName]: next });
		} else {
			const { [fieldName]: _, ...rest } = current;
			form.setFieldValue('aiTranslated', rest);
		}
	};
	const autoApproveListeners = (fieldName: string) =>
		isEditing ? { onChange: () => approveTranslation(fieldName) } : undefined;
	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		form.handleSubmit();
	};
	const basicFields = ['name', 'description', 'objectives', 'activities'];
	const infoFields = [
		'impactEquivalent',
		'beneficiariesDescription',
		'claim',
		'shortParagraph',
		'moreInfo',
		'donationAmounts',
	];
	const multimediaFields = ['coverImageAlt', 'coverVideoAlt', 'otherImages'];
	const fundingFields = [] as string[];
	const changeLang = (langCode: string | undefined) => {
		navigate({
			search: (prev) => ({
				...prev,
				plang: langCode === project.defaultLanguage ? undefined : langCode,
			}),
		});
	};

	return (
		<FormReadOnlyProvider value={!isEditing}>
			<form.Subscribe selector={(state) => state.values.aiTranslated}>
				{(aiTranslated) => {
					const basicLangs = getAiLangsForFields(basicFields, aiTranslated);
					const infoLangs = getAiLangsForFields(infoFields, aiTranslated);
					const multimediaLangs = getAiLangsForFields(multimediaFields, aiTranslated);
					const fundingLangs = getAiLangsForFields(fundingFields, aiTranslated);
					const aiHint = (langs: string[]) =>
						langs.length > 0
							? (active: boolean) => (
									<AiPendingHint
										langs={langs}
										onLangClick={changeLang}
										size="sm"
										variant={active ? 'active' : 'default'}
									/>
								)
							: undefined;
					return (
						<VerticalTabs
							tabs={[
								{ value: 'funding' as const, label: t`Funding channels`, hint: aiHint(fundingLangs) },
								{ value: 'languages' as const, label: t`Languages` },
								{ value: 'basic' as const, label: t`Key information`, hint: aiHint(basicLangs) },
								{ value: 'info' as const, label: t`Project Details`, hint: aiHint(infoLangs) },
								{ value: 'multimedia' as const, label: t`Multimedia`, hint: aiHint(multimediaLangs) },
							]}
							activeTab={activeTab}
							onTabChange={(value) =>
								navigate({ search: (prev) => ({ ...prev, tab: value === 'funding' ? undefined : value }) })
							}
						/>
					);
				}}
			</form.Subscribe>
			<div className="min-h-0 flex-1 overflow-y-auto pt-2 contain-layout md:overflow-y-scroll md:pt-4">
				<form
					id="project-edit-form"
					onSubmit={handleSubmit}
					className="flex w-full max-w-[600px] flex-col gap-6 px-4 py-4 md:px-8 md:py-6"
				>
					{activeTab === 'funding' && (
						<ProjectFundingTab form={form} isEditing={isEditing} autoOpenAddToPortal={shouldOpenAddToPortal} />
					)}
					{activeTab === 'basic' && (
						<ProjectBasicTab
							form={form}
							selectedLang={selectedLang}
							isEditing={isEditing}
							approveTranslation={approveTranslation}
							autoApproveListeners={autoApproveListeners}
							languageDropdown={
								<LanguageDropdown
									languages={activeLanguages}
									selectedLang={selectedLang}
									defaultLanguage={activeDefaultLang}
									onLanguageChange={changeLang}
									size="sm"
								/>
							}
						/>
					)}
					{activeTab === 'info' && (
						<ProjectInfoTab
							form={form}
							selectedLang={selectedLang}
							isEditing={isEditing}
							approveTranslation={approveTranslation}
							autoApproveListeners={autoApproveListeners}
							languageDropdown={
								<LanguageDropdown
									languages={activeLanguages}
									selectedLang={selectedLang}
									defaultLanguage={activeDefaultLang}
									onLanguageChange={changeLang}
									size="sm"
								/>
							}
						/>
					)}
					{activeTab === 'multimedia' && (
						<ProjectMultimediaTab
							form={form}
							selectedLang={selectedLang}
							isEditing={isEditing}
							approveTranslation={approveTranslation}
							coverImageUrl={optimizedCoverImageUrl}
							coverVideoUrl={coverVideoUrl}
						/>
					)}
					{activeTab === 'languages' && (
						<Card className="shadow-warm">
							<CardHeader>
								<CardTitle className="text-xl">{t`Languages`}</CardTitle>
							</CardHeader>
							<CardContent className="flex flex-col gap-2">
								<p className="text-muted-foreground text-sm">
									{t`The default language is the one that will be used to create the project. We will automatically translate the details of your project to the selected languages and you'll be able to review those translations later.`}
								</p>
								<form.Subscribe selector={(state) => state.values.defaultLanguage}>
									{(defaultLanguage) => (
										<form.AppField name="languages">
											{(field) => (
												<field.LanguagesPickerField
													defaultText={t`Default`}
													setAsDefaultText={t`Set as default`}
													defaultLanguage={defaultLanguage}
													onDefaultLanguageChange={(lang) => form.setFieldValue('defaultLanguage', lang)}
													onLanguageRemoved={(langCode) => {
														for (const { path, record } of collectTranslatableEntries(
															form.state.values as Record<string, unknown>,
														)) {
															if (record[langCode]) {
																const { [langCode]: _, ...rest } = record;
																form.setFieldValue(path as keyof typeof form.state.values, rest);
															}
														}
														const ai = form.getFieldValue('aiTranslated');
														const updatedAi: Record<string, string[]> = {};
														for (const [field, langs] of Object.entries(ai)) {
															const filtered = langs.filter((l) => l !== langCode);
															if (filtered.length > 0) updatedAi[field] = filtered;
														}
														form.setFieldValue('aiTranslated', updatedAi);
														// If the removed language was currently viewed, switch to default
														if (selectedLang === langCode) {
															const newDefault = form.getFieldValue('defaultLanguage') as string;
															navigate({
																search: (prev) => ({
																	...prev,
																	plang: prev.plang == null ? newDefault : undefined,
																}),
															});
														}
													}}
												/>
											)}
										</form.AppField>
									)}
								</form.Subscribe>
							</CardContent>
						</Card>
					)}
				</form>
			</div>
			{isEditing && (
				<EditFooter
					form={form}
					formId="project-edit-form"
					portalId="project-footer-portal"
					onDiscard={() => {
						navigate({ search: (prev) => ({ ...prev, editing: undefined }) });
					}}
					onDelete={project.status !== 'active' ? () => setShowDeleteDialog(true) : undefined}
				/>
			)}
			<AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t`Delete Project`}</AlertDialogTitle>
						<AlertDialogDescription>{t`Are you sure you want to delete this project? This action cannot be undone.`}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel variant="outline-dark">{t`Cancel`}</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							disabled={deleteMutation.isPending}
							onClick={async (e) => {
								e.preventDefault();
								try {
									await deleteMutation.mutateAsync({ projectId });
								} catch {
									toast.error(
										project.status === 'active'
											? t`Active projects can't be deleted. Pause the project first.`
											: project.status === 'funded'
												? t`Funded projects can't be deleted.`
												: t`Failed to delete project. Please try again.`,
									);
									setShowDeleteDialog(false);
									return;
								}
								// Remove project and list cache to ensure fresh data on the list page
								queryClient.removeQueries({ queryKey: trpc.ngo.getProject.queryKey({ projectId }) });
								queryClient.removeQueries({ queryKey: trpc.ngo.getProjects.queryKey() });
								setShowDeleteDialog(false);
								if (fromPortal) {
									navigate({
										to: '/ngos/portals/$portalId',
										params: { portalId: fromPortal },
										search: { tab: 'projects' },
									});
								} else {
									navigate({ to: '/ngos/projects' });
								}
							}}
						>
							{deleteMutation.isPending ? t`Deleting...` : t`Delete`}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</FormReadOnlyProvider>
	);
}
